import React from 'react';
import PropTypes from 'prop-types';
import { TableCell, TableRow } from '@tempmee/react';
import history from '../../../../history';
import { UserAdapters } from '../../../../domain/Users';
import { RouteLink, isModifiedClick } from '../../../commonComponents/RouteLink';
import { TableCardFrame } from '../../../commonComponents/TableCardFrame';
import { locationUserPath } from '../paths';

const COLUMNS = ['Name', 'Email', 'Phone', 'Location Count', 'Last Active'];

/**
 * The Location Users list, in the shared `TableCardFrame`. No card title for
 * the reason GotuAdminsTable gives: the page header already names the table.
 *
 * The whole row opens the identity. The name is also a real link, so the row
 * has a keyboard path and cmd/middle-click opens a new tab; the row's handler
 * leaves a click on that link to the link, or it would navigate twice.
 */
export const LocationUsersTable = ({
  users,
  locationCounts,
  isLocationCountsError,
  isPending,
  isError,
  hasSearch,
  footer,
}) => (
  <TableCardFrame
    columns={COLUMNS}
    rowCount={users.length}
    isPending={isPending}
    isError={isError}
    emptyMessage={hasSearch ? 'No Location Users match this search.' : 'No Location Users yet.'}
    errorMessage="Could not load Location Users."
    footer={footer}
  >
    {users.map((user) => (
      <TableRow
        key={user.id}
        hover
        className="cursor-pointer"
        onClick={(event) => {
          if (isModifiedClick(event) || event.target.closest('a')) return;
          history.push(locationUserPath(user.id));
        }}
      >
        <TableCell>
          <RouteLink to={locationUserPath(user.id)}>{user.name}</RouteLink>
        </TableCell>
        <TableCell>{user.email}</TableCell>
        <TableCell>{user.phoneLabel}</TableCell>
        <TableCell>
          {UserAdapters.toLocationCountLabel(locationCounts[user.id], isLocationCountsError)}
        </TableCell>
        <TableCell>{user.lastActiveLabel}</TableCell>
      </TableRow>
    ))}
  </TableCardFrame>
);

LocationUsersTable.propTypes = {
  users: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      email: PropTypes.string.isRequired,
      phoneLabel: PropTypes.string.isRequired,
      lastActiveLabel: PropTypes.string.isRequired,
    }),
  ).isRequired,
  /** Location Count per identity id; an id with no entry has not loaded yet. */
  locationCounts: PropTypes.objectOf(PropTypes.number).isRequired,
  isLocationCountsError: PropTypes.bool.isRequired,
  isPending: PropTypes.bool.isRequired,
  isError: PropTypes.bool.isRequired,
  /** Picks the empty copy: "nothing matched" reads as a bug with no search typed. */
  hasSearch: PropTypes.bool,
  /** Footer slot — the page passes its pagination. */
  footer: PropTypes.node,
};

LocationUsersTable.defaultProps = { hasSearch: false, footer: null };

export default LocationUsersTable;
